import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * The Bungie login as the page sees it. The worker (/api/session) keeps the long-lived part in a cookie the page
 * can't read and hands out a short-lived access token; this keeps that token and asks for a new one before it runs out.
 */
export interface SessionState {
  status: 'loading' | 'signedOut' | 'signedIn';
  membershipId?: string;
  /** A valid access token, renewed first if it is about to run out. Undefined when signed out. */
  getToken: () => Promise<string | undefined>;
  signIn: () => void;
  signOut: () => Promise<void>;
}

interface Token { accessToken: string; expiresAt: number; membershipId: string }

const Ctx = createContext<SessionState | null>(null);

async function fetchSession(): Promise<Token | null> {
  const r = await fetch('/api/session', { credentials: 'same-origin', cache: 'no-store' });
  if (!r.ok) return null;
  const j = await r.json();
  return j.signedIn ? (j as Token) : null;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionState['status']>('loading');
  const [membershipId, setMembershipId] = useState<string>();
  const token = useRef<Token | null>(null);
  const pending = useRef<Promise<Token | null> | null>(null);

  const refresh = useCallback(async () => {
    // one request at a time, however many parts of the page ask together
    pending.current ??= fetchSession().finally(() => (pending.current = null));
    const t = await pending.current.catch(() => null);
    token.current = t;
    setStatus(t ? 'signedIn' : 'signedOut');
    setMembershipId(t?.membershipId);
    return t;
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const getToken = useCallback(async () => {
    const t = token.current && token.current.expiresAt - Date.now() > 60_000 ? token.current : await refresh();
    return t?.accessToken;
  }, [refresh]);

  const signIn = useCallback(() => {
    const back = window.location.pathname + window.location.search;
    window.location.href = `/auth/login?return=${encodeURIComponent(back)}`;
  }, []);

  const signOut = useCallback(async () => {
    await fetch('/auth/logout', { method: 'POST', credentials: 'same-origin' });
    token.current = null;
    setStatus('signedOut');
    setMembershipId(undefined);
  }, []);

  return <Ctx.Provider value={{ status, membershipId, getToken, signIn, signOut }}>{children}</Ctx.Provider>;
}

export function useSession(): SessionState {
  const s = useContext(Ctx);
  if (!s) throw new Error('useSession outside SessionProvider');
  return s;
}
